/**
 * TasksTabContent
 * Full task management view with responsive split-view layout
 * Uses URL-based routing: /dashboard/tasks, /dashboard/tasks/view/:taskId, /dashboard/tasks/create
 */

import { useState, useMemo, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useActiveTasks, useCompletedTasks, useDeleteTask, useToggleTaskStar, useCompleteTask, useRestoreTask } from '@/hooks/use-tasks';
import {
  CheckSquare,
  CheckCircle2,
  Clock,
  Sparkles,
  ChevronRight,
  Plus,
  ListTodo,
  AlertTriangle,
  CalendarClock,
  Trash2,
  ArrowUpDown,
  Layers,
  FileText,
  BookOpen,
  PenLine,
  GraduationCap,
  Brain,
  RotateCcw,
  HelpCircle,
  Search,
  X,
  Star,
  Undo2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { UserTask, TaskType } from '@/types/task';
import { slugToName } from '@/lib/subject-mapping';
import { TaskDetailPanel } from './TaskDetailPanel';

type FilterType = 'active' | 'past_due' | 'upcoming' | 'completed';
type SortType = 'due_date' | 'priority' | 'created_at';

// Helper to parse date string as local date (fixes timezone offset issue)
// When DB stores "2024-01-15", we want to display Jan 15, not Jan 14
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper to format date and optionally time in local time
const formatLocalDateTime = (dateString: string): string => {
  const hasTime = dateString.includes('T') && dateString.split('T')[1]?.length > 0;

  if (hasTime) {
    // Parse date part
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    const date = new Date(year, month - 1, day, hour, minute);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  return parseLocalDate(dateString).toLocaleDateString();
};

const SORT_OPTIONS: { value: SortType; label: string }[] = [
  { value: 'due_date', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'created_at', label: 'Created' },
];

interface TasksTabContentProps {
  className?: string;
}

/**
 * Main Tasks Tab Content - URL-based routing for split view
 */
export function TasksTabContent({ className }: TasksTabContentProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <Routes>
        <Route index element={<TaskListView />} />
        <Route path="view/:taskId" element={<TaskSplitView />} />
        <Route path="create" element={<TaskSplitView isCreate />} />
      </Routes>
    </div>
  );
}

/**
 * Task list view - main task listing with filters
 */
function TaskListView() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('active');
  const [sortBy, setSortBy] = useState<SortType>('due_date');
  const [groupBySubject, setGroupBySubject] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const { data: activeTasks = [], isLoading: loadingActive } = useActiveTasks();
  const { data: completedTasks = [], isLoading: loadingCompleted } = useCompletedTasks(20);
  const completeTask = useCompleteTask();
  const restoreTask = useRestoreTask();
  const deleteTask = useDeleteTask();
  const toggleStar = useToggleTaskStar();

  const isLoading = loadingActive || loadingCompleted;

  // Helper to check if a task is past due
  const isPastDue = useCallback((task: UserTask) => {
    if (!task.due_date || task.status === 'completed') return false;
    const taskDate = parseLocalDate(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return taskDate < today;
  }, []);

  // Helper to check if a task is upcoming (has future due date)
  const isUpcoming = useCallback((task: UserTask) => {
    if (!task.due_date || task.status === 'completed') return false;
    const taskDate = parseLocalDate(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return taskDate >= today;
  }, []);

  // Helper to filter tasks by search query
  const matchesSearch = useCallback((task: UserTask, query: string) => {
    if (!query.trim()) return true;
    const searchLower = query.toLowerCase();
    const titleMatch = task.title.toLowerCase().includes(searchLower);
    const descMatch = task.description?.toLowerCase().includes(searchLower) ?? false;
    const subjectLabel = task.related_subject
      ? slugToName(task.related_subject) || ''
      : '';
    const subjectMatch = subjectLabel.toLowerCase().includes(searchLower);
    return titleMatch || descMatch || subjectMatch;
  }, []);

  // Calculate counts for each filter
  const counts = useMemo(() => {
    const active = activeTasks.length;
    const pastDue = activeTasks.filter(isPastDue).length;
    const upcoming = activeTasks.filter(isUpcoming).length;
    const completed = completedTasks.length;
    return { active, pastDue, upcoming, completed };
  }, [activeTasks, completedTasks, isPastDue, isUpcoming]);

  // Sort function - starred items always come first
  const sortTasks = useCallback((tasks: UserTask[]) => {
    return [...tasks].sort((a, b) => {
      // Starred items always come first
      if (a.is_starred !== b.is_starred) {
        return a.is_starred ? -1 : 1;
      }

      switch (sortBy) {
        case 'due_date':
          // Tasks with no due date go to end
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return parseLocalDate(a.due_date).getTime() - parseLocalDate(b.due_date).getTime();
        case 'priority':
          // Higher priority first (3=urgent, 0=low)
          return b.priority - a.priority;
        case 'created_at':
          // Newest first
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });
  }, [sortBy]);

  // Filter and sort tasks based on selected filter and search query
  const filteredTasks = useMemo(() => {
    let tasks: UserTask[];
    switch (filter) {
      case 'active':
        tasks = activeTasks;
        break;
      case 'past_due':
        tasks = activeTasks.filter(isPastDue);
        break;
      case 'upcoming':
        tasks = activeTasks.filter(isUpcoming);
        break;
      case 'completed':
        tasks = completedTasks;
        break;
      default:
        tasks = activeTasks;
    }
    // Apply search filter
    if (searchQuery.trim()) {
      tasks = tasks.filter(task => matchesSearch(task, searchQuery));
    }
    return sortTasks(tasks);
  }, [filter, activeTasks, completedTasks, sortBy, searchQuery, isPastDue, isUpcoming, matchesSearch, sortTasks]);

  // Group tasks by subject
  const groupedTasks = useMemo(() => {
    if (!groupBySubject) return null;

    const groups: Record<string, UserTask[]> = {};
    const noSubject: UserTask[] = [];

    filteredTasks.forEach((task) => {
      if (task.related_subject) {
        if (!groups[task.related_subject]) {
          groups[task.related_subject] = [];
        }
        groups[task.related_subject].push(task);
      } else {
        noSubject.push(task);
      }
    });

    // Sort groups by subject label
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      const labelA = slugToName(a) || a;
      const labelB = slugToName(b) || b;
      return labelA.localeCompare(labelB);
    });

    // Add "No Subject" group at the end if there are any
    if (noSubject.length > 0) {
      sortedGroups.push(['_no_subject', noSubject]);
    }

    return sortedGroups;
  }, [filteredTasks, groupBySubject]);

  const handleComplete = (taskId: string) => {
    completeTask.mutate({ taskId });
  };

  const handleRestore = (taskId: string) => {
    restoreTask.mutate(taskId);
  };

  const handleDelete = () => {
    if (deleteTaskId) {
      deleteTask.mutate(deleteTaskId);
      setDeleteTaskId(null);
    }
  };

  const handleToggleStar = (taskId: string, isStarred: boolean) => {
    toggleStar.mutate({ taskId, isStarred });
  };

  const handleViewTask = (task: UserTask) => {
    navigate(`/dashboard/tasks/view/${task.id}`);
  };

  const getTaskIcon = (type: TaskType) => {
    switch (type) {
      case 'study_session':
        return BookOpen;
      case 'reading':
        return BookOpen;
      case 'outline':
        return FileText;
      case 'essay':
        return PenLine;
      case 'flashcards':
        return GraduationCap;
      case 'practice':
        return Brain;
      case 'review_topic':
        return RotateCcw;
      case 'diagnostic':
        return Sparkles;
      case 'other':
      default:
        return HelpCircle;
    }
  };

  const getPriorityStyles = (task: UserTask) => {
    // Check if past due first
    if (isPastDue(task)) return 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20';
    // Then check priority: 0=low, 1=normal, 2=high, 3=urgent
    if (task.priority >= 3) return 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20';
    if (task.priority >= 2) return 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20';
    return 'border-l-gray-300 dark:border-l-gray-600';
  };

  // AI suggestions when no tasks (memoized to avoid recreating on each render)
  const suggestions = useMemo(() => [
    {
      title: 'Practice your weakest topic',
      description: 'Based on your performance insights',
      route: '/flashcards/study',
      icon: Sparkles,
    },
    {
      title: 'Run a diagnostic assessment',
      description: 'Identify new areas to focus on',
      route: '/diagnostic',
      icon: CheckSquare,
    },
    {
      title: 'Review recent mistakes',
      description: 'Strengthen your understanding',
      route: '/flashcards/study',
      icon: Clock,
    },
  ], []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title - Gold Standard Pattern */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tasks</h1>
        <p className="text-gray-500 dark:text-gray-400">
          {counts.active} active task{counts.active === 1 ? '' : 's'}
          {counts.pastDue > 0 && ` • ${counts.pastDue} past due`}
        </p>
      </div>

      {/* Filter Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <button
          onClick={() => setFilter('active')}
          aria-pressed={filter === 'active'}
          className={cn(
            'p-3 rounded-lg border-2 transition-all text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            filter === 'active'
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-muted-foreground/30'
          )}
        >
          <div className="flex items-center gap-2">
            <ListTodo className={cn('h-5 w-5', filter === 'active' ? 'text-primary' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-lg font-bold">{counts.active}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setFilter('past_due')}
          aria-pressed={filter === 'past_due'}
          className={cn(
            'p-3 rounded-lg border-2 transition-all text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
            filter === 'past_due'
              ? 'border-red-500 bg-red-50/50 dark:bg-red-950/20'
              : 'border-border hover:border-muted-foreground/30'
          )}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn('h-5 w-5', filter === 'past_due' ? 'text-red-500' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-lg font-bold">{counts.pastDue}</p>
              <p className="text-xs text-muted-foreground">Past Due</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setFilter('upcoming')}
          aria-pressed={filter === 'upcoming'}
          className={cn(
            'p-3 rounded-lg border-2 transition-all text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            filter === 'upcoming'
              ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
              : 'border-border hover:border-muted-foreground/30'
          )}
        >
          <div className="flex items-center gap-2">
            <CalendarClock className={cn('h-5 w-5', filter === 'upcoming' ? 'text-blue-500' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-lg font-bold">{counts.upcoming}</p>
              <p className="text-xs text-muted-foreground">Upcoming</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setFilter('completed')}
          aria-pressed={filter === 'completed'}
          className={cn(
            'p-3 rounded-lg border-2 transition-all text-left focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
            filter === 'completed'
              ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20'
              : 'border-border hover:border-muted-foreground/30'
          )}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className={cn('h-5 w-5', filter === 'completed' ? 'text-green-500' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-lg font-bold">{counts.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </div>
        </button>
      </div>

      {/* Search & Controls */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 py-2">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 h-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-transparent"
              aria-label="Clear search"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>

        {/* Sort & Group Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortType)}>
              <SelectTrigger className="w-[130px] h-8">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="group-by-subject"
              checked={groupBySubject}
              onCheckedChange={setGroupBySubject}
            />
            <Label htmlFor="group-by-subject" className="flex items-center gap-1 text-sm cursor-pointer">
              <Layers className="h-4 w-4 text-muted-foreground" />
              Group by Subject
            </Label>
          </div>
        </div>
      </div>

      {/* Task List */}
      {filteredTasks.length > 0 ? (
        groupBySubject && groupedTasks ? (
          <div className="space-y-6">
            {groupedTasks.map(([subject, tasks]) => {
              const subjectLabel = subject === '_no_subject'
                ? 'No Subject'
                : slugToName(subject) || subject;
              return (
                <div key={subject}>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    {subjectLabel}
                    <Badge variant="secondary" className="text-xs">{tasks.length}</Badge>
                  </h3>
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        isPastDue={isPastDue(task)}
                        getTaskIcon={getTaskIcon}
                        getPriorityStyles={getPriorityStyles}
                        onView={handleViewTask}
                        onComplete={handleComplete}
                        onRestore={handleRestore}
                        onDelete={setDeleteTaskId}
                        onToggleStar={handleToggleStar}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isPastDue={isPastDue(task)}
                getTaskIcon={getTaskIcon}
                getPriorityStyles={getPriorityStyles}
                onView={handleViewTask}
                onComplete={handleComplete}
                onRestore={handleRestore}
                onDelete={setDeleteTaskId}
                onToggleStar={handleToggleStar}
              />
            ))}
          </div>
        )
      ) : (
        /* Empty state with AI suggestions */
        <Card className="border-dashed">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-base">You're all caught up!</CardTitle>
            <p className="text-sm text-muted-foreground">
              No pending tasks. Here are some suggestions:
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {suggestions.map((suggestion, i) => (
              <Link
                key={i}
                to={suggestion.route}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
              >
                <suggestion.icon className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{suggestion.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {suggestion.description}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
            {/* Add Task CTA */}
            <div className="pt-2 border-t mt-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/dashboard/tasks/create')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add a Custom Task
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTaskId} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Task split view - shows task list on left, detail/editor on right (desktop)
 * On mobile, shows only the detail view with back navigation
 */
function TaskSplitView({ isCreate = false }: { isCreate?: boolean }) {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { data: activeTasks = [], isLoading } = useActiveTasks();
  const { data: completedTasks = [] } = useCompletedTasks(20);

  // Find the task
  const allTasks = [...activeTasks, ...completedTasks];
  const task = taskId ? allTasks.find(t => t.id === taskId) : null;

  const handleBack = () => {
    navigate('/dashboard/tasks');
  };

  const handleSave = () => {
    navigate('/dashboard/tasks');
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-muted rounded-lg" />;
  }

  // If viewing a specific task but it doesn't exist, redirect to list
  if (taskId && !task && !isLoading) {
    return <Navigate to="/dashboard/tasks" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header - matches Flashcard create page style */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {isCreate ? 'Create New Task' : 'Edit Task'}
        </h1>
      </div>

      {/* Task detail panel */}
      <TaskDetailPanel
        task={isCreate ? undefined : task}
        onSave={handleSave}
        onCancel={handleBack}
      />
    </div>
  );
}

// Task card component for reuse in grouped/ungrouped views
interface TaskCardProps {
  task: UserTask;
  isPastDue: boolean;
  getTaskIcon: (type: TaskType) => typeof CheckSquare;
  getPriorityStyles: (task: UserTask) => string;
  onView: (task: UserTask) => void;
  onComplete: (taskId: string) => void;
  onRestore: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onToggleStar: (taskId: string, isStarred: boolean) => void;
}

function TaskCard({
  task,
  isPastDue: taskIsPastDue,
  getTaskIcon,
  getPriorityStyles,
  onView,
  onComplete,
  onRestore,
  onDelete,
  onToggleStar,
}: TaskCardProps) {
  const TaskIcon = getTaskIcon(task.task_type);

  return (
    <Card
      className={cn(
        'border-l-4 transition-colors cursor-pointer hover:bg-muted/50',
        getPriorityStyles(task)
      )}
      onClick={() => onView(task)}
    >
      <CardContent className="flex items-center gap-4 p-4">
        {/* Star button */}
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleStar(task.id, !task.is_starred)}
            className={cn(
              'h-10 w-10 md:h-8 md:w-8 p-0',
              task.is_starred
                ? 'text-amber-500 hover:text-amber-600'
                : 'text-muted-foreground hover:text-amber-500'
            )}
            aria-label={task.is_starred ? 'Unstar task' : 'Star task'}
          >
            <Star className={cn('h-5 w-5 md:h-4 md:w-4', task.is_starred && 'fill-current')} />
          </Button>
        </div>
        <div className="shrink-0">
          <TaskIcon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{task.title}</p>
          {task.description && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              {task.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {/* Priority badge for high/urgent */}
            {task.priority >= 3 && (
              <Badge variant="destructive" className="text-xs">
                Urgent
              </Badge>
            )}
            {task.priority === 2 && (
              <Badge className="text-xs bg-amber-500 hover:bg-amber-500 text-white">
                High Priority
              </Badge>
            )}
            {task.due_date && (
              <Badge
                variant={taskIsPastDue ? 'destructive' : 'outline'}
                className="text-xs"
              >
                <Clock className="h-3 w-3 mr-1" />
                {formatLocalDateTime(task.due_date)}
                {taskIsPastDue && ' (Past due)'}
              </Badge>
            )}
            {task.source === 'syllabus' && (
              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                <FileText className="h-3 w-3 mr-1" />
                Syllabus
              </Badge>
            )}
            {task.related_subject && (
              <Badge variant="outline" className="text-xs">
                {slugToName(task.related_subject) || task.related_subject}
              </Badge>
            )}
            {task.estimated_minutes && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                ~{task.estimated_minutes}min
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <TooltipProvider delayDuration={300}>
            {task.status === 'completed' ? (
              /* Restore button for completed tasks */
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRestore(task.id)}
                    className="h-10 w-10 md:h-8 md:w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    aria-label="Restore to active"
                  >
                    <Undo2 className="h-5 w-5 md:h-4 md:w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Restore to active</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              /* Complete and Delete buttons for active tasks */
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onComplete(task.id)}
                      className="h-10 w-10 md:h-8 md:w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                      aria-label="Mark as complete"
                    >
                      <CheckCircle2 className="h-5 w-5 md:h-4 md:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Mark as complete</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(task.id)}
                      className="h-10 w-10 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-red-50 dark:hover:bg-red-900/20"
                      aria-label="Delete task"
                    >
                      <Trash2 className="h-5 w-5 md:h-4 md:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete task</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}

export default TasksTabContent;
